import React, { useCallback } from 'react';

import { HStack, Stack, Text } from '@chakra-ui/react';
import { useModals } from '@saas-ui/react';

import { CloseButton, DrawerTitle } from '@/components/common';
import { Button } from '@/components/core';
import { FONTS_OPACITY } from '@/constant';

export interface ModalConfig {
  id: string;
  title?: string | React.ReactNode;
  body?: React.ReactNode;
  size?:
    | 'xs'
    | 'sm'
    | 'md'
    | 'lg'
    | 'xl'
    | '2xl'
    | '3xl'
    | '4xl'
    | '5xl'
    | '6xl'
    | 'full';
  contentProps?: Record<string, unknown>;
  overlayProps?: Record<string, unknown>;
  hideCloseButton?: boolean;
  hideCustomCloseButton?: boolean;
  headerProps?: Record<string, unknown>;
  scrollBehavior?: 'inside' | 'outside';
  onClose?: () => void;
  onOpen?: () => void;
  footer?: React.ReactNode;
  footerProps?: Record<string, unknown>;
  isCentered?: boolean;
  closeOnEsc?: boolean;
  closeOnOverlayClick?: boolean;
  autoFocus?: boolean;
  /** Turn off for modals that open a third-party dialog, e.g. Google Picker. */
  trapFocus?: boolean;
}

export interface AlertConfig {
  id: string;
  title?: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void | Promise<void>;
  onCancel?: () => void;
  type?: 'info' | 'warning' | 'error' | 'success';
}

export interface ConfirmConfig extends ModalConfig {
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm?: () => void | Promise<void>;
  onCancel?: () => void;
}

export interface DrawerConfig {
  id: string;
  title?: string | React.ReactNode;
  description?: React.ReactNode;
  body?: React.ReactNode;
  size?:
    | 'xs'
    | 'sm'
    | 'md'
    | 'lg'
    | 'xl'
    | '2xl'
    | '3xl'
    | '4xl'
    | '5xl'
    | '6xl'
    | 'full';
  contentProps?: Record<string, unknown>;
  overlayProps?: Record<string, unknown>;
  placement?: 'left' | 'right' | 'top' | 'bottom';
  hideCloseButton?: boolean; // This prop is for saas-modal library close button
  headerProps?: Record<string, unknown>;
  scrollBehavior?: 'inside' | 'outside';
  onClose?: () => void;
  onEsc?: () => void;
  onOpen?: () => void;
  footer?: React.ReactNode;
  footerProps?: Record<string, unknown>;
  closeOnEsc?: boolean;
  closeOnOverlayClick?: boolean;
}

export const useModal = () => {
  const modals = useModals();

  const closeModal = useCallback(
    (id: string) => {
      modals.close(id);
    },
    [modals]
  );

  const openModal = useCallback(
    (config: ModalConfig) => {
      const {
        id,
        title,
        body,
        size = 'md',
        contentProps = {},
        overlayProps = {},
        hideCloseButton = true,
        hideCustomCloseButton = false,
        headerProps = {},
        scrollBehavior = 'inside',
        onClose,
        onOpen,
        footer,
        footerProps = {},
        isCentered = true,
        closeOnEsc = true,
        closeOnOverlayClick = true,
        autoFocus = true,
        trapFocus = true,
      } = config;

      // If title is a string, wrap it with default styling using JSX directly
      const processedTitle =
        typeof title === 'string' ? (
          <HStack gap={2.5}>
            <Text size='headingSecondary' flex={1} noOfLines={1}>
              {title}
            </Text>
            {!hideCustomCloseButton && (
              <CloseButton onClick={() => closeModal(id)} />
            )}
          </HStack>
        ) : (
          title
        );

      modals.open({
        id,
        title: processedTitle,
        body,
        size,
        contentProps,
        overlayProps,
        hideCloseButton,
        headerProps: {
          borderBottom: '1px solid',
          borderColor: 'border.light',
          padding: '6',
          ...headerProps,
        },
        scrollBehavior,
        onClose: () => {
          onClose?.();
        },
        onOpen,
        footer,
        footerProps,
        isCentered,
        closeOnEsc,
        closeOnOverlayClick,
        autoFocus,
        trapFocus,
      });

      onOpen?.();
    },
    [modals, closeModal]
  );

  const openAlert = useCallback(
    (config: AlertConfig) => {
      const {
        id,
        title = 'Alert',
        description,
        confirmText = 'OK',
        onConfirm,
        type = 'info',
      } = config;

      modals.alert({
        id,
        title,
        description,
        confirmText,
        onConfirm: async () => {
          await onConfirm?.();
        },
        type,
      });
    },
    [modals]
  );

  const openConfirm = useCallback(
    (config: ConfirmConfig) => {
      const {
        id,
        title = 'Confirm',
        description,
        body,
        contentProps = {},
        headerProps = {},
        footerProps = {},
        confirmLabel = 'Confirm',
        cancelLabel = 'Cancel',
        onConfirm,
        onCancel,
        isCentered = true,
        closeOnEsc = false,
        closeOnOverlayClick = false,
        ...rest
      } = config;

      const handleCancelClick = () => {
        onCancel?.();
        closeModal(id);
      };

      const handleConfirmClick = () => {
        onConfirm?.();
        closeModal(id);
      };

      const titleSection = (
        <Stack gap={4}>
          <Text size='headingPrimary'>{title}</Text>
          {description && <Text opacity={FONTS_OPACITY}>{description}</Text>}
        </Stack>
      );

      const footerSection = (
        <HStack>
          <Button onClick={handleCancelClick}>{cancelLabel}</Button>
          <Button variant='secondary' onClick={handleConfirmClick}>
            {confirmLabel}
          </Button>
        </HStack>
      );

      modals.open({
        id,
        hideCloseButton: true,
        title: titleSection,
        body,
        footer: footerSection,
        contentProps: {
          padding: 8,
          ...contentProps,
        },
        headerProps: {
          padding: 0,
          ...headerProps,
        },
        footerProps: {
          padding: 0,
          marginTop: 8,
          justifyContent: 'flex-start',
          ...footerProps,
        },
        confirmLabel,
        cancelLabel,
        onConfirm,
        onCancel,
        isCentered,
        closeOnEsc,
        closeOnOverlayClick,
        ...rest,
      });
    },
    [closeModal, modals]
  );

  const openDrawer = useCallback(
    (config: DrawerConfig) => {
      const {
        id,
        title,
        description,
        body,
        contentProps = {},
        overlayProps = {},
        size = 'md',
        placement = 'right',
        hideCloseButton = false,
        headerProps = {},
        scrollBehavior = 'inside',
        onClose,
        onEsc,
        onOpen,
        footer,
        footerProps = {},
        closeOnEsc = false, // By default, we don't want to close the drawer on ESC key
        closeOnOverlayClick = true,
      } = config;

      const titleSection = (
        <DrawerTitle
          title={title}
          description={description}
          hideCloseButton={hideCloseButton}
          onClose={onClose}
        />
      );

      modals.drawer({
        id,
        title: titleSection,
        body,
        contentProps,
        overlayProps,
        size,
        placement,
        hideCloseButton: true,
        headerProps,
        scrollBehavior,
        onClose,
        onOpen,
        footer,
        footerProps,
        scope: 'myscope',
        closeOnEsc,
        closeOnOverlayClick,
        onEsc,
      });

      onOpen?.();
    },
    [modals, closeModal]
  );

  return {
    openModal,
    closeModal,
    openAlert,
    openConfirm,
    openDrawer,
  };
};
